import { useEffect } from 'react';

import type { RefObject } from 'react';

/**
 * Shadow DOM–safe click-outside helper. Uses `e.composedPath()` so the check
 * works even when the ref lives inside a shadow root (where `e.target` gets
 * retargeted to the custom-element host by the time the event bubbles to
 * `document`, and `ref.current.contains(e.target)` is always false).
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  handler: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    function handleMouseDown(e: MouseEvent) {
      const node = ref.current;
      if (!node) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.includes(node)) return;
      if (!path.length && e.target instanceof Node && node.contains(e.target)) return;
      handler();
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [ref, handler, enabled]);
}
